/**
 * Consultas e gravações de Residências pelo TanStack Query.
 *
 * Papel: aqui ficam `useLancamentosResidencia`, que mantém em cache as casas de
 * topo e os lançamentos, e `useSalvarLancamentoResidencia` e
 * `useExcluirLancamentoResidencia`, que gravam e mandam a listagem se ler de
 * novo.
 *
 * Como usar: toda chave deste módulo começa por `CHAVE_RESIDENCIAS`. As duas
 * mutações leem a listagem de novo no `onSettled`, com sucesso **e** com falha:
 * depois de um 409 de casa que deixou de existir, o `<select>` precisa refletir
 * o cadastro de agora, e depois de uma remoção que falhou porque outra pessoa já
 * tinha removido, a linha precisa sumir. O `mutateAsync` só resolve depois que
 * essa releitura termina, e é por isso que a caixa, fechada depois dele, sai com
 * a tabela já atualizada.
 *
 * `habilitado` existe porque `PaginaResidencias` monta o menu da Fazenda Boa
 * Vista e os blocos juntos, e quem chega só pelas chaves do menu não alcança
 * `residencias`: hook não se chama condicionalmente, e uma consulta que saísse
 * de qualquer jeito voltaria 403 a cada abertura da tela para essa pessoa.
 */
import { type NovoLancamentoResidencia } from '@concierge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  atualizarLancamento,
  buscarLancamentos,
  criarLancamento,
  excluirLancamento,
} from '../api/residencias';

/** Prefixo de todas as chaves de cache deste módulo. */
export const CHAVE_RESIDENCIAS = ['residencias'] as const;

/** Chave da listagem de casas de topo e lançamentos. */
export const CHAVE_LANCAMENTOS = [...CHAVE_RESIDENCIAS, 'lancamentos'] as const;

/**
 * Lê as casas de topo e todos os lançamentos.
 *
 * @param habilitado - Se a consulta deve mesmo ir à API; a página passa falso
 *   para quem não alcança `ver` em `residencias`.
 * @returns Resultado da consulta, com `casas` e `lancamentos`.
 */
export function useLancamentosResidencia(habilitado: boolean) {
  return useQuery({
    queryKey: CHAVE_LANCAMENTOS,
    queryFn: ({ signal }) => buscarLancamentos(signal),
    enabled: habilitado,
  });
}

/** Variáveis de `useSalvarLancamentoResidencia`. */
interface SalvarLancamentoVariaveis {
  /** Identificador do lançamento a alterar, ou `null` para criar um novo. */
  id: string | null;
  dados: NovoLancamentoResidencia;
}

/**
 * Cria ou altera um lançamento, conforme venha identificador.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a gravação não responde.
 */
export function useSalvarLancamentoResidencia() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dados }: SalvarLancamentoVariaveis) =>
      id === null ? criarLancamento(dados) : atualizarLancamento(id, dados),
    onSettled: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_LANCAMENTOS }),
  });
}

/**
 * Apaga um lançamento.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a exclusão não responde.
 */
export function useExcluirLancamentoResidencia() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => excluirLancamento(id),
    onSettled: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_LANCAMENTOS }),
  });
}
